/**
 * @prettier
 */
import React, { useState, useEffect, useRef, useMemo, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

// Components from ocrVerification
import { ocrVerification } from "../../config/ocrVerification.js";
import { ACTION_LABELS } from "../../config/dataDogActionLabels.js";
import TextInput from "../EverificationPage/components/TextInput";
import SelectInput from "../EverificationPage/components/SelectInput";
import DateInput from "../EverificationPage/components/DateInput";
import PhoneNumberInput from "../EverificationPage/components/PhoneNumberInput";
import AddressLookupInput from "../EverificationPage/components/AddressLookupInput";
import Navigator from "../../components/Navigator.jsx";
import { LoadingStateAnimation } from "../../components/ProcessingLayout";
import Header from "../../components/Header";
import StageComplete from "../../components/Messages/StageComplete";
import CustomButton from "../../components/CustomButton";
import {
    validateOcrVerification,
    validateCustomField,
} from "../../services/shared/ocrVerificationService.js";
import {
    getCountries,
    getDocumentOcrDetails,
    setSelectedCountry,
    setAutoAddressFocus,
    resetEVerification,
    setFormValueV2,
    setPhone,
    setErrors,
    setDateErrorFocus,
    storeAddressV2,
    submitEVerificationV2,
} from "../../state/actions/eVerificationActions";
import { completeStage } from "../../state/actions/submissionStateActions";
import { clearEVerificationAddress } from "../../state/actions/addressLookupActions";
import SubmissionFlowStepper from "../../components/SubmissionFlowStepper/index.jsx";

const ACTION = ACTION_LABELS.eVerification;
const DATE_FORMAT =
    process.env.REACT_APP_IS_US_REGION === "true" ? "mdy" : "dmy";

const OcrVerificationPage = ({
    countries = [],
    getCountries,
    getDocumentOcrDetails,
    customFields = [],
    activatedCountries = [],
    addressLookupActive,
    addressLookupHidden,
    selectedCountry = {},
    setSelectedCountry,
    setFormValueV2,
    setPhone,
    setErrors,
    setDateErrorFocus,
    submitEVerificationV2,
    storeAddressV2,
    clearEVerificationAddress,
    setAutoAddressFocus,
    resetEVerification,
    branding,
    pendingItems,
    ocrVerificationCompleted,
    companyName,
    completeStage,
    customerAppPreferences,
    submissionState,
    // Form fields from Redux
    firstName,
    lastName,
    dob,
    phone,
    email,
    phonePrefix,
    phoneRaw,
    address1,
    address2,
    city,
    county,
    postCode,
    customField,
    errors: reduxErrors,
}) => {
    const { t } = useTranslation();
    const formRef = useRef(null);
    const isMountedRef = useRef(true);
    const pendingNavigationRef = useRef(false);
    const [processing, setProcessing] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [navigation, setNavigation] = useState({
        action: "load",
        props: null,
    });
    const [isAutoAddressOpen, setIsAutoAddressOpen] = useState(false);
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [customFieldValue, setCustomFieldValue] = useState(() =>
        customFields.length > 0 ? customFields[0].value : null
    );
    const [ocrDataFetched, setOcrDataFetched] = useState(false);
    const [formKey, setFormKey] = useState(0); // Key to force form re-render when OCR data loads

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Navigate only after Redux marks OCR complete (avoids routing back to
    // /ocr_verification because navigateNext read stale submission state).
    useEffect(() => {
        if (pendingNavigationRef.current && ocrVerificationCompleted) {
            pendingNavigationRef.current = false;
            setNavigation({
                action: "next",
            });
        }
    }, [ocrVerificationCompleted]);

    useEffect(() => {
        // Reset eVerification state
        if (submissionState.screens.e_verification.enabled === 0) {
            resetEVerification();
        }

        if (countries.length === 0) {
            getCountries();
        }

        setAutoAddressFocus(false);

        // Set initial address lookup state based on selected country
        if (selectedCountry.supports_address_lookup) {
            setAutoAddressFocus(true);
        }

        // Set document title for OCR Verification page
        document.title = t("idpal_ocr_verification_title");

        // Get the customer preferences for showing sections
        const showPersonalInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Personal Info"
            )?.value === 1;
        const showContactInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Contact Info"
            )?.value === 1;
        const showAddressInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Address Info"
            )?.value === 1;

        // Skip validation for sections that aren't shown
        if (!showPersonalInfo && !showContactInfo) {
            if (!showAddressInfo) {
                submitForm();
            } else {
                setActiveStep(1);
            }
        }
    }, []);

    // Function to populate OCR data
    const populateOcrData = async () => {
        // Prevent duplicate calls
        if (ocrDataFetched) {
            return;
        }
        try {
            const ocrData = await getDocumentOcrDetails();
            if (ocrData) {
                // Update form fields using existing setFormValueV2 action
                if (ocrData.first_name) {
                    setFormValueV2("firstName", ocrData.first_name);
                }

                if (ocrData.last_name) {
                    setFormValueV2("lastName", ocrData.last_name);
                }

                if (ocrData.date_of_birth) {
                    // Expected input: YYYY-MM-DD
                    const dobParts = ocrData.date_of_birth.split('-');
                    let formattedDob = ocrData.date_of_birth;
                    if (dobParts.length === 3) {
                        const [year, month, day] = dobParts;
                        if (DATE_FORMAT === 'mdy') {
                            // MM/DD/YYYY (US)
                            formattedDob = `${month}/${day}/${year}`;
                        } else {
                            // DD/MM/YYYY (Non-US)
                            formattedDob = `${day}/${month}/${year}`;
                        }
                    }
                    setFormValueV2("dob", formattedDob);
                }

                // Handle country separately - need to find matching country object and set selectedCountry
                if (ocrData.country) {
                    const matchedCountry = countries.find(
                        country =>
                            country.name.toLowerCase() ===
                            ocrData.country.toLowerCase()
                    );

                    if (
                        matchedCountry &&
                        matchedCountry.name !== selectedCountry.name
                    ) {
                        setSelectedCountry(
                            matchedCountry.iso_3166_3,
                            countries
                        );
                        setFormValueV2("country", matchedCountry.name);

                        // Set phone prefix based on country
                        if (matchedCountry.iso_3166_2) {
                            const phonePrefix = `+${matchedCountry.iso_3166_2}`;
                            setFormValueV2("phonePrefix", phonePrefix);
                        }
                    }
                }

                if (ocrData.address_one) {
                    setFormValueV2("address1", ocrData.address_one);
                }

                if (ocrData.address_two) {
                    setFormValueV2("address2", ocrData.address_two);
                }

                if (ocrData.city) {
                    setFormValueV2("city", ocrData.city);
                }

                if(ocrData.county) {
                    setFormValueV2('county', ocrData.county);
                }
                
                if (ocrData.post_code) {
                    setFormValueV2("postCode", ocrData.post_code);
                }
            }

            if (!isMountedRef.current) {
                return;
            }

            // Mark as fetched to prevent duplicate calls
            setOcrDataFetched(true);
            // Increment form key to force re-render of form inputs with new values
            setFormKey(prev => prev + 1);
        } catch (error) {
            // Silently fail - OCR pre-population is not critical
            if (isMountedRef.current) {
                setOcrDataFetched(true);
            }
        }
    };

    // Fetch OCR details after countries are loaded (only once)
    useEffect(() => {
        if (countries.length > 0 && !ocrDataFetched) {
            populateOcrData();
        }
    }, [countries, ocrDataFetched]);

    // Handle country selection effects
    useEffect(() => {
        if (!selectedCountry.supports_address_lookup) {
            setIsFirstRender(false);
        }

        // Handle address lookup when country changes
        if (selectedCountry.supports_address_lookup) {
            clearEVerificationAddress();
            setAutoAddressFocus(true);

            if (isFirstRender) {
                setIsAutoAddressOpen(true);
                setIsFirstRender(false);
                setAutoAddressFocus(false);
            }
        }
    }, [selectedCountry]);

    // Handle custom field value
    useEffect(() => {
        if (customField.length > 0 && customField !== customFieldValue) {
            setCustomFieldValue(customField);
        }
    }, [customField]);

    const validateStep = step => {
        // Get the customer preferences for validation
        const showPersonalInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Personal Info"
            )?.value === 1;
        const showContactInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Contact Info"
            )?.value === 1;
        const showAddressInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Address Info"
            )?.value === 1;

        const personalInfoOptional =
            customerAppPreferences.find(
                pref => pref.title === "Personal Information Optional"
            )?.value === 1;
        const contactInfoOptional =
            customerAppPreferences.find(
                pref => pref.title === "Contact Information Optional"
            )?.value === 1;
        const addressInfoOptional =
            customerAppPreferences.find(
                pref => pref.title === "Address Information Optional"
            )?.value === 1;

        // Prepare step data based on customer preferences
        let stepData = {};

        if (step === 0) {
            // Only include personal info fields if they should be shown
            if (showPersonalInfo) {
                stepData = {
                    ...stepData,
                    firstName,
                    lastName,
                    dob: formatDateValue(dob),
                };
            }

            // Only include contact info fields if they should be shown
            if (showContactInfo) {
                stepData = {
                    ...stepData,
                    phone,
                    email,
                };
            }
        } else {
            // Only include address info if it should be shown
            if (showAddressInfo) {
                stepData = {
                    country: selectedCountry.name,
                    address1,
                    address2,
                    city,
                    county,
                    postCode,
                    customField,
                };
            }
        }

        // Use the improved validation service with step-specific validation
        // and pass the optional flags to the validation service
        const validationErrors = validateOcrVerification(
            stepData,
            customFields,
            step, // Pass the step number for specific validation
            {
                personalInfoOptional,
                contactInfoOptional,
                addressInfoOptional,
                showPersonalInfo,
                showContactInfo,
                showAddressInfo,
            }
        );

        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            setFocus();
            if (step === 0 && validationErrors.dob) {
                setDateErrorFocus(true);
            }
        }

        return validationErrors;
    };

    const setFocus = () => {
        if (!reduxErrors || !formRef.current) return;

        const firstErrorKey = Object.keys(reduxErrors)[0];
        if (!firstErrorKey) return;

        const errorInput = formRef.current.querySelector(`#${firstErrorKey}`);
        if (errorInput) {
            errorInput.scrollIntoView({ behavior: "smooth", block: "center" });
            setTimeout(() => errorInput.focus(), 100);
        }
    };

    const formatDateValue = (value, format = DATE_FORMAT) => {
        if (!value) return "";

        const dateParts = value.split("/");
        if (dateParts.length !== 3) return value;

        if (format === "mdy") {
            const [month, day, year] = dateParts;
            return `${year}-${month}-${day}`;
        } else {
            const [day, month, year] = dateParts;
            return `${year}-${month}-${day}`;
        }
    };

    const handleDateChange = event => {
        const { value } = event.target;
        setFormValueV2("dob", value); // Store display format in Redux

        // Get the customer preferences for validation
        const personalInfoOptional =
            customerAppPreferences.find(
                pref => pref.title === "Personal Information Optional"
            )?.value === 1;
        const showPersonalInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Personal Info"
            )?.value === 1;

        // Format for validation
        const formattedDate = formatDateValue(value);

        // Validate date
        const errors = validateOcrVerification(
            {
                dob: formattedDate,
            },
            [],
            null,
            {
                personalInfoOptional,
                showPersonalInfo,
            }
        );

        setErrors(errors);
    };

    const handleInputChange = event => {
        const { id: name, value } = event.target;
        setFormValueV2(name, value);

        // Get the customer preferences for validation
        const personalInfoOptional =
            customerAppPreferences.find(
                pref => pref.title === "Personal Information Optional"
            )?.value === 1;
        const contactInfoOptional =
            customerAppPreferences.find(
                pref => pref.title === "Contact Information Optional"
            )?.value === 1;
        const addressInfoOptional =
            customerAppPreferences.find(
                pref => pref.title === "Address Information Optional"
            )?.value === 1;

        const showPersonalInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Personal Info"
            )?.value === 1;
        const showContactInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Contact Info"
            )?.value === 1;
        const showAddressInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Address Info"
            )?.value === 1;

        // Create validation data with context
        const validationData = {
            [name]: value,
        };

        // Add country context for address fields
        if (["address1", "address2", "city", "postCode"].includes(name)) {
            validationData.country = selectedCountry.name;
        }

        // Validate and update errors
        const errors = validateOcrVerification(
            validationData,
            customFields,
            null,
            {
                personalInfoOptional,
                contactInfoOptional,
                addressInfoOptional,
                showPersonalInfo,
                showContactInfo,
                showAddressInfo,
            }
        );

        setErrors(errors);
    };

    const handlePhoneChange = (phone, prefix, raw) => {
        setPhone(phone, prefix, raw);

        // Get the customer preferences for validation
        const contactInfoOptional =
            customerAppPreferences.find(
                pref => pref.title === "Contact Information Optional"
            )?.value === 1;
        const showContactInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Contact Info"
            )?.value === 1;

        // Validate phone number
        const errors = validateOcrVerification({ phone }, [], null, {
            contactInfoOptional,
            showContactInfo,
        });

        setErrors(errors);
    };

    const handleCountryChange = event => {
        const selectedIso = event.target.value;
        clearEVerificationAddress();

        // Get the customer preferences for validation
        const addressInfoOptional =
            customerAppPreferences.find(
                pref => pref.title === "Address Information Optional"
            )?.value === 1;
        const showAddressInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Address Info"
            )?.value === 1;

        // Find selected country
        const country = countries.find(c => c.iso_3166_3 === selectedIso);
        setSelectedCountry(selectedIso, countries);

        // Update form value
        setFormValueV2("country", country?.name || "");

        // Reset custom field
        setCustomFieldValue("");
        setFormValueV2("customField", "");

        setErrors(prev => {
            const newErrors = { ...prev };
            // Clear all address-related errors
            ['address1', 'address2', 'city', 'postCode', 'customField', 'country', 'county'].forEach(field => {
                delete newErrors[field];
            });

            // Validate new country selection
            const countryErrors = validateOcrVerification(
                {
                    country: country?.name,
                },
                [],
                null,
                {
                    addressInfoOptional,
                    showAddressInfo,
                }
            );

            if (countryErrors.country) {
                newErrors.country = countryErrors.country;
            }

            return newErrors;
        });

        // Handle address lookup visibility
        if (country?.supports_address_lookup) {
            setIsAutoAddressOpen(true);
            setAutoAddressFocus(true);
        } else {
            setIsAutoAddressOpen(false);
            setAutoAddressFocus(true);
        }
    };

    const handleCustomFieldChange = event => {
        const { value } = event.target;
        setCustomFieldValue(value);
        setFormValueV2("customField", value);

        // Get the customer preferences for validation
        const addressInfoOptional =
            customerAppPreferences.find(
                pref => pref.title === "Address Information Optional"
            )?.value === 1;

        // Only validate if we're on the address step
        if (activeStep === 1) {
            const errors = validateCustomField(
                selectedCountry.name,
                value,
                customFields,
                addressInfoOptional
            );

            setErrors(errors);
        }
    };

    const showAddressLookup = useMemo(
        () => addressLookupActive && !addressLookupHidden && isAutoAddressOpen,
        [addressLookupActive, isAutoAddressOpen, addressLookupHidden]
    );

    const handleContinue = event => {
        event?.preventDefault?.();

        // Normal validation flow
        const validationErrors = validateStep(activeStep);
        const showAddressInfo =
            customerAppPreferences.find(
                pref => pref.title === "Show Address Info"
            )?.value === 1;

        if (activeStep === 0 && Object.keys(validationErrors).length === 0) {
            if (!showAddressInfo) {
                // If address info isn't shown, skip validation and proceed with submission
                submitForm();
            } else {
                setActiveStep(1);
            }
        } else if (
            activeStep === 1 &&
            Object.keys(validationErrors).length === 0
        ) {
            submitForm();
        } else {
            // If there are validation errors, stay on the current step
        }
    };

    const submitForm = () => {
        showSpinner();
        // Clear any prior error overlay before starting a new submit
        setNavigation({
            action: "load",
            props: null,
        });
        // Format date to ISO 8601
        let ISODateFormat = "";
        if (dob) {
            if (DATE_FORMAT === "mdy") {
                const [month, day, year] = dob.split("/");
                ISODateFormat = `${year}-${month}-${day}`;
            } else {
                // Default to dmy
                const [day, month, year] = dob.split("/");
                ISODateFormat = `${year}-${month}-${day}`;
            }
        }

        const formData = {
            firstName:  firstName ?  firstName : '',
            lastName:  lastName ?  lastName : '',
            email: email ? email : '',
            dob:  ISODateFormat ?  ISODateFormat : '',
            address1:  address1 ?  address1 : '',
            address2:  address2 ?  address2 : '',
            city:  city ?  city : '',
            county:  county ?  county : '',
            postCode:  postCode ?  postCode : '',
            country:  selectedCountry.name
                ?  selectedCountry.name
                : '',
            customField:  customField ?  customField : '',
            phone:  phone ?  phone : '',
            phonePrefix:  phonePrefix ?  phonePrefix : '',
            phoneRaw:  phoneRaw ?  phoneRaw : '',
        };

        // Submit data — wait for API success before completing the stage
        const requests = [];
        if (
            activatedCountries.includes(selectedCountry.name) &&
            customerAppPreferences.find(
                pref => pref.title === "Enable Address Database Checking"
            )?.value
        ) {
            requests.push(
                submitEVerificationV2(formData, handleSubmitErrorCallback)
            );
        }
        requests.push(storeAddressV2(formData, handleSubmitErrorCallback));

        Promise.all(requests)
            .then(() => {
                if (!isMountedRef.current) {
                    return;
                }
                completeStage("ocr_verification");
                pendingNavigationRef.current = true;
            })
            .catch(() => {
                // Real API failures are surfaced by handleSubmitErrorCallback
            });
    };

    const handleSubmitErrorCallback = (error, data) => {
        // Ignore only after unmount (e.g. user already left the page).
        // Do not suppress failures just because submit was started —
        // verification/address may not have been stored.
        if (!isMountedRef.current) {
            return;
        }

        setProcessing(false);
        pendingNavigationRef.current = false;
        setNavigation({
            action: "error",
            props: {
                retryAction: () => handleContinue(),
                error: error,
            },
        });
    };

    const showSpinner = () => {
        setProcessing(true);
    };

    // Handle loading state (still show in-page error overlay if submit failed,
    // and keep Navigator available when advancing to the next stage)
    if (
        pendingItems > 0 &&
        navigation.action !== "error" &&
        navigation.action !== "next"
    ) {
        return (
            <div className="u-text-center start-loading">
                <LoadingStateAnimation />
            </div>
        );
    }

    // Handle completion state — keep Navigator mounted when advancing so
    // action "next" can route to liveness/consent instead of getting stuck.
    if (ocrVerificationCompleted && navigation.action !== "next") {
        return (
            <>
                <Header />
                <StageComplete
                    message={t("idpal_submission_complete", {
                        company: companyName,
                    })}
                    hideContinue={true}
                    hideButton={true}
                />
            </>
        );
    }

    // Post-submit: waiting on navigation / finalise
    if (processing || navigation.action === "next") {
        return (
            <Fragment>
                <div className="u-text-center start-loading">
                    <LoadingStateAnimation />
                </div>
                <Navigator
                    page={"ocr_verification"}
                    action={navigation.action}
                    propsToPass={navigation.props}
                />
            </Fragment>
        );
    }

    return (
        <>
            <div className="root-layout-container e-verification-page">
                <Header />
                <div className="form-step-title-container">
                    <SubmissionFlowStepper />
                </div>

                <form
                    autoComplete="off"
                    noValidate
                    onSubmit={handleContinue}
                    ref={formRef}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "32px",
                        height: "inherit",
                    }}
                >
                    {activeStep === 0 && (
                        <>
                            <div className="form-step-header">
                                <h1 className="form-step-title">
                                    {t("idpal_confirm_personal_details")}
                                </h1>
                            </div>
                            {/* Personal Information Section */}
                            {customerAppPreferences.find(
                                pref => pref.title === "Show Personal Info"
                            )?.value === 1 && (
                                <>
                                    <TextInput
                                        key={`firstName-${formKey}`}
                                        config={ocrVerification.ocrFirstName}
                                        label={t("idpal_first_name")}
                                        defaultValue={firstName}
                                        error={reduxErrors.firstName}
                                        onChange={handleInputChange}
                                        actionDataLabel={ACTION.firstNameInput}
                                        isRequired={
                                            !customerAppPreferences.find(
                                                pref =>
                                                    pref.title ===
                                                    "Personal Information Optional"
                                            )?.value
                                        }
                                    />
                                    <TextInput
                                        key={`lastName-${formKey}`}
                                        config={ocrVerification.ocrLastName}
                                        label={t("idpal_last_name")}
                                        defaultValue={lastName}
                                        error={reduxErrors.lastName}
                                        onChange={handleInputChange}
                                        actionDataLabel={ACTION.lastNameInput}
                                        isRequired={
                                            !customerAppPreferences.find(
                                                pref =>
                                                    pref.title ===
                                                    "Personal Information Optional"
                                            )?.value
                                        }
                                    />
                                    <DateInput
                                        key={`dob-${formKey}`}
                                        config={ocrVerification.ocrDob}
                                        label={t("idpal_dob")}
                                        error={reduxErrors.dob}
                                        defaultValue={dob}
                                        dateFormat={DATE_FORMAT}
                                        onChange={handleDateChange}
                                        tabIndex={0}
                                        isRequired={
                                            !customerAppPreferences.find(
                                                pref =>
                                                    pref.title ===
                                                    "Personal Information Optional"
                                            )?.value
                                        }
                                    />
                                </>
                            )}

                            {/* Contact Information Section */}
                            {customerAppPreferences.find(
                                pref => pref.title === "Show Contact Info"
                            )?.value === 1 && (
                                <>
                                    <PhoneNumberInput
                                        key={`phone-${formKey}`}
                                        config={ocrVerification.ocrPhone}
                                        label={t("idpal_phone")}
                                        error={reduxErrors.phone}
                                        onChange={handlePhoneChange}
                                        defaultValue={phone}
                                        initialCountry={
                                            selectedCountry.iso_3166_2
                                        }
                                        isRequired={
                                            !customerAppPreferences.find(
                                                pref =>
                                                    pref.title ===
                                                    "Contact Information Optional"
                                            )?.value
                                        }
                                    />
                                    <TextInput
                                        key={`email-${formKey}`}
                                        config={ocrVerification.ocrEmail}
                                        label={t("idpal_email")}
                                        defaultValue={email || ""}
                                        error={reduxErrors.email}
                                        onChange={handleInputChange}
                                        actionDataLabel={ACTION.emailInput}
                                        isRequired={
                                            !customerAppPreferences.find(
                                                pref =>
                                                    pref.title ===
                                                    "Contact Information Optional"
                                            )?.value
                                        }
                                    />
                                </>
                            )}
                            <div className="e-verification-page-cta-container">
                                <CustomButton
                                    id="finish-first-step"
                                    className="btn btn__arrow"
                                    label={t("idpal_continue")}
                                />
                            </div>
                        </>
                    )}

                    {activeStep === 1 && customerAppPreferences.find(pref => pref.title === "Show Address Info")?.value === 1 && (
                        <>
                        <div className="form-step-header">
                            <h1 className="form-step-title">
                                {t('idpal_confirm_address')}
                            </h1>
                        </div>
                            <SelectInput
                                config={ocrVerification.ocrCountry}
                                error={reduxErrors.country}
                                label={t('idpal_country')}
                                placeholder={t('idpal_country')}
                                onChange={handleCountryChange}
                                options={countries}
                                defaultValue={selectedCountry.iso_3166_3}
                                actionDataLabel={ACTION.countryInput}
                                isRequired={!customerAppPreferences.find(pref => pref.title === "Address Information Optional")?.value}
                            />
                            {showAddressLookup ? (
                                <AddressLookupInput
                                    onChangeCustomField={handleCustomFieldChange}
                                    customFieldValue={customFieldValue}
                                />
                            ) : (
                                <>
                                    <TextInput
                                        key={`address1-${selectedCountry.iso_3166_3 || ''}-${formKey}`}
                                        config={ocrVerification.ocrAddress1}
                                        error={reduxErrors.address1}
                                        onChange={handleInputChange}
                                        label={t('idpal_address_1')}
                                        placeholder={t('idpal_address_1')}
                                        defaultValue={address1}
                                        actionDataLabel={ACTION.address1Input}
                                        isRequired={!customerAppPreferences.find(pref => pref.title === "Address Information Optional")?.value}
                                    />
                                    <TextInput
                                        key={`address2-${selectedCountry.iso_3166_3 || ''}-${formKey}`}
                                        config={ocrVerification.ocrAddress2}
                                        error={reduxErrors.address2}
                                        onChange={handleInputChange}
                                        label={t('idpal_address_2')}
                                        placeholder={t('idpal_address_2')}
                                        defaultValue={address2}
                                        actionDataLabel={ACTION.address2Input}
                                    />
                                    <TextInput
                                        key={`city-${selectedCountry.iso_3166_3 || ''}-${formKey}`}
                                        config={ocrVerification.ocrCity}
                                        error={reduxErrors.city}
                                        onChange={handleInputChange}
                                        label={t('idpal_city')}
                                        placeholder={t('idpal_city')}
                                        defaultValue={city}
                                        actionDataLabel={ACTION.cityInput}
                                        isRequired={!customerAppPreferences.find(pref => pref.title === "Address Information Optional")?.value}
                                    />
                                    <TextInput
                                        key={`county-${selectedCountry.iso_3166_3 || ''}-${formKey}`}
                                        config={ocrVerification.ocrCounty}
                                        error={reduxErrors.county}
                                        onChange={handleInputChange}
                                        label={t('idpal_county')}
                                        placeholder={t('idpal_county')}
                                        defaultValue={county}
                                        actionDataLabel={ACTION.countyInput}
                                    />
                                    <TextInput
                                        key={`postcode-${selectedCountry.iso_3166_3 || ''}-${formKey}`}
                                        config={ocrVerification.ocrPostCode}
                                        error={reduxErrors.postCode}
                                        onChange={handleInputChange}
                                        label={t('idpal_postcode')}
                                        placeholder={t('idpal_postcode')}
                                        actionDataLabel={ACTION.postcodeInput}
                                        defaultValue={postCode}
                                        isRequired={!customerAppPreferences.find(pref => pref.title === "Address Information Optional")?.value}
                                    />
                                    {/* Custom fields based on country selection */}
                                    {customFields.map(field => {
                                        if (selectedCountry.iso_3166_3 === field.iso) {
                                            return (
                                                <TextInput
                                                    key={`${field.iso}-${formKey}`}
                                                    config={field}
                                                    error={reduxErrors.customField}
                                                    onChange={handleCustomFieldChange}
                                                    actionDataLabel={ACTION.customField}
                                                    isRequired={field.required}
                                                    label={field.name}
                                                    defaultValue={customFieldValue}
                                                    placeholder={field.name}
                                                />
                                            )
                                        }
                                        return null;
                                    })}
                                </>
                            )}
                            <div className='e-verification-page-cta-container'>
                                <CustomButton
                                    id='finish-second-step'
                                    className='btn btn__arrow'
                                    label={t("idpal_continue")}
                                />
                            </div>
                        </>
                    )}
                </form>
            </div>
            <Navigator
                page={"ocr_verification"}
                action={navigation.action}
                propsToPass={navigation.props}
            />
        </>
    );
};

const mapStateToProps = state => ({
    countries: state.eVerification?.countries || [],
    customFields: state.eVerification?.customFields || [],
    selectedCountry: state.eVerification?.selectedCountry || {},
    activatedCountries: state.eVerification.activatedCountries,
    addressLookupHidden: state.addressLookup?.hidden || false,
    addressLookupActive: state.addressLookup?.active || false,
    branding: state.config?.profile || {},
    pendingItems: state.spinner?.pendingItems || 0,
    ocrVerificationCompleted:
        state.submissionState?.submissionState?.ocr_verification?.completed ||
        false,
    companyName: state.config?.profile?.data?.company_branding?.name || "",
    customerAppPreferences:
        state.config?.profile?.data?.customer_app_preferences || [],
    // Form fields from Redux state with safe defaults
    firstName: state.eVerification?.firstName || '',
    lastName: state.eVerification?.lastName || '',
    dob: state.eVerification?.dob || '',
    phone: state.eVerification?.phone || '',
    phonePrefix: state.eVerification?.phonePrefix || '',
    phoneRaw: state.eVerification?.phoneRaw || '',
    address1: state.eVerification?.address1 || '',
    address2: state.eVerification?.address2 || '',
    city: state.eVerification?.city || '',
    county: state.eVerification?.state || '',
    postCode: state.eVerification?.postCode || '',
    customField: state.eVerification?.customField || '',
    errors: state.eVerification?.errors || {},
    submissionState: state.submissionState || {},
    email: state.eVerification?.email || "",
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getCountries,
            getDocumentOcrDetails,
            setSelectedCountry,
            setAutoAddressFocus,
            resetEVerification,
            setDateErrorFocus,
            clearEVerificationAddress,
            setFormValueV2,
            setPhone,
            setErrors,
            submitEVerificationV2,
            storeAddressV2,
            completeStage,
        },
        dispatch
    );

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(OcrVerificationPage);
